/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useRef } from 'react'
import { compareVersions } from 'compare-versions'
import classnames from 'classnames'
import { inject, observer } from 'mobx-react'
import { useIntl, getLocale } from 'umi'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import { useSize, useInViewport } from '@umijs/hooks'
import { useMemoizedFn, useDocumentVisibility, useDebounceFn as useDebounceFna } from 'ahooks'
import { Icon, Avatar, Tooltip } from '@/components'
import PersonLabel from '@/components/PersonLabel'
import { MODE } from '@/models/meeting'
import { RootStore, focused } from '@/models'
import { InterpretationStatus } from '@/models/simultaneousInterpretation'

import { DEVICE_TYPE } from '@/types/meeting'
import Style from './index.less'
import { WebRTC2 } from '@/utils/NIM'
import EmoJ from '../EmoJ'
import { ViewBoxEvent, eventBus } from '@/utils/event'
import { ua } from '@/utils/deviceCheck'
import { RoleType } from '../PeoplePanel/pc'

const oldVersions = {
  [DEVICE_TYPE.IPHONE]: '3.29.0',
  [DEVICE_TYPE.IPAD]: '3.29.0',
  [DEVICE_TYPE.MAC]: '3.23.0',
  [DEVICE_TYPE.PC]: '3.23.0',
  [DEVICE_TYPE.ANDROID]: '3.23.0',
}

const avatarSizeMaxHeight = 68 / 128

export enum viewBoxType {
  video = 'video',
  screen = 'screen',
}
export enum sizeTypes {
  small = 'small',
  large = 'large',
  full = 'full',
}

export interface ViewBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  meeting?: RootStore['meeting']
  speakWeight?: RootStore['speakWeight']
  rtc?: RootStore['rtc']
  user?: RootStore['user']
  members?: RootStore['members']
  simultaneous?: RootStore['simultaneousInterpretation']
  yxuid: string
  type: string
  sizeType?: sizeTypes
  className?: string
  avatarSize?: number
  showTalking?: boolean
  showEmoJ?: boolean
}

const ViewBox: React.FC<ViewBoxProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    rtc: store.rtc,
    user: store.user,
    members: store.members,
    simultaneous: store.simultaneousInterpretation,
    speakWeight: store.speakWeight,
  }
})(
  observer((props: ViewBoxProps) => {
    const {
      simultaneous,
      members,
      meeting,
      speakWeight,
      rtc,
      user,
      yxuid,
      type = viewBoxType.video,
      sizeType = sizeTypes.large,
      className,
      showEmoJ = true,
      avatarSize = sizeType === sizeTypes.small ? 48 : 100,
      showTalking = true,
      ...otherProps
    } = props
    const { formatMessage } = useIntl()
    const [playing, setPlaying] = React.useState(false)
    const [{ width: _vWidth, height: _vHeight }, videoRef] = useSize<HTMLDivElement>()
    // 4.6.40版本 限制了宽高必须是整数 后续待定
    const vWidth = Math.floor(_vWidth || 0)
    const vHeight = Math.floor(_vHeight || 0)
    const [inViewPort, viewRef] = useInViewport<HTMLDivElement>()
    const docVisibility = useDocumentVisibility()
    const [{ height: bHeight }] = useSize<HTMLDivElement>(viewRef.current)

    const firstPlayRef = useRef<boolean>(true)
    const isTalking = React.useMemo(() => {
      return showTalking && yxuid === String(meeting?.speakerYxUid) && meeting?.speakerName
    }, [meeting?.speakerYxUid, yxuid, showTalking, meeting?.speakerName])

    const rtcInfo = React.useMemo(() => {
      const isMySelf = yxuid === user?.yxuid
      const remoteStream = rtc?.remoteStreamsMap.get(Number(yxuid))
      const info = meeting?.membersMap.get(yxuid)
      const data = {
        yxuid,
        deviceType: info?.deviceType,
        appVersion: info?.appVersion,
        isShareScreen: isMySelf ? rtc?.isShareScreen : !!remoteStream?.screen,
        isOpenVideo: isMySelf ? rtc?.isOpenVideo : !!remoteStream?.video,
        isOpenAudio: isMySelf
          ? rtc?.isOpenAudio
          : (!!remoteStream?.audio && !rtc?.muteMap.get(yxuid.toString())) ||
            members?.interpretersAudioStatusMap.get(yxuid.toString()),
        name: info?.memberName || '',
        role: info?.role || '',
        avatarUrl: info?.memberAvatarUrl || '',
        avatarId: Number(info?.memberAvatarId || 0),
        isMySelf,
        stream: isMySelf ? rtc?.localStream : remoteStream,
        isBackCamera: isMySelf ? rtc?.isBackCamera : false,
        isBadNetwork: rtc?.networkQuality.has(String(yxuid)) ? !rtc.networkQuality.get(String(yxuid)) : false,
      }
      return data
    }, [
      yxuid,
      rtc?.muteMap.get(yxuid.toString()),
      meeting?.membersMap,
      meeting?.membersMap.get(yxuid),
      rtc?.remoteStreamsMap.get(Number(yxuid)),
      rtc?.remoteStreamsMap.get(Number(yxuid))?.update,
      rtc?.remoteStreamsMap.get(Number(yxuid))?.audio,
      rtc?.remoteStreamsMap.get(Number(yxuid))?.video,
      rtc?.remoteStreamsMap.get(Number(yxuid))?.screen,
      rtc?.isShareScreen,
      rtc?.isOpenVideo,
      rtc?.isOpenAudio,
      user?.yxuid,
      members?.interpretersAudioStatusMap.get(yxuid.toString()),
    ])

    // eslint-disable-next-line consistent-return
    const vType = React.useMemo(() => {
      // 根据发布流的用户的appVersion，来判断是否主辅流渲染
      // 如果版本号低于"3.27.0",则使用原来的逻辑渲染屏幕共享；
      // 如果版本号高于或等于"3.27.0",则使用主辅流的逻辑渲染屏幕共享和视频。
      // @ts-ignore
      if (rtcInfo.isMySelf || !oldVersions[rtcInfo.deviceType]) return type
      // @ts-ignore
      if (rtcInfo.appVersion && compareVersions(rtcInfo.appVersion, oldVersions[rtcInfo.deviceType]) < 1) {
        const screenYxUid = String(meeting?.screenUserInfo?.yxUid)
        if (screenYxUid === String(rtcInfo.yxuid)) {
          if (type === viewBoxType.screen) {
            return viewBoxType.video
          }
          return viewBoxType.screen
        }
      }
      return type
    }, [rtcInfo, rtcInfo.appVersion, type, rtcInfo.isOpenVideo, rtcInfo.isShareScreen, meeting?.screenUid])

    const ablePlay = useMemo(() => {
      // 判断当前是视频流是否订阅；
      if (rtcInfo.isMySelf) {
        return vType === 'video' ? rtcInfo.isOpenVideo : rtcInfo.isShareScreen
      }
      return !!rtcInfo.stream?.pubStatus?.[vType]?.consumerId
    }, [rtcInfo, vType])

    // 格式化窗大小

    const { run: initBoxSizeD } = useDebounceFna(
      async () => {
        if (!inViewPort) {
          return
        }
        if (!ablePlay) return
        const { isMySelf, stream } = rtcInfo
        // HACK 获取dom 有dom表示播放成功
        const videoDom = stream.Play?.video?.dom
        const screenDom = stream.Play?.screen?.dom
        const sdom = vType === viewBoxType.screen ? screenDom : videoDom
        if (sdom && vType === viewBoxType.screen) {
          // console.log('ViewBoxEvent.SDOMCREATE', sdom.videoWidth, sdom.videoHeight);
          eventBus.emit(ViewBoxEvent.SDOMCREATE, {
            width: sdom.videoWidth,
            height: sdom.videoHeight,
          })
        }
        if (!sdom) {
          play()
          return
        }
        if (!playing) {
          if (sdom) {
            if (isMySelf) {
              // 解析视频流第一帧后的回调
              sdom.oncanplay = async () => {
                const { width, height } = await rtc!.getLocalVideoStats(
                  vWidth || 0,
                  vHeight || 0,
                  sdom.videoWidth,
                  sdom.videoHeight,
                )

                stream.setLocalRenderMode(
                  {
                    width,
                    height,
                    cut: false,
                  },
                  vType,
                )
                sdom.oncanplay = null
                setPlaying(true)
              }
            } else {
              sdom.oncanplay = async () => {
                const { width, height } = await rtc!.getRemoteVideoStats(
                  vWidth || 0,
                  vHeight || 0,
                  String(stream.streamID),
                  sdom.videoWidth,
                  sdom.videoHeight,
                )

                stream.setRemoteRenderMode(
                  {
                    width,
                    height,
                    cut: false,
                  },
                  vType,
                )
                sdom.oncanplay = null
                setPlaying(true)
              }
            }
          }
        }
        // 这里获取video标签播放的视频的实际的分辨率
        // eslint-disable-next-line no-lonely-if
        if (sdom) {
          if (isMySelf) {
            // 解析视频流第一帧后的回调
            const { width, height } = rtc!.calcVideoSize(vWidth || 0, vHeight || 0, sdom.videoWidth, sdom.videoHeight)

            stream.setLocalRenderMode(
              {
                width,
                height,
                cut: false,
              },
              vType,
            )
          } else {
            const { width, height } = rtc!.calcVideoSize(vWidth || 0, vHeight || 0, sdom.videoWidth, sdom.videoHeight)
            stream.setRemoteRenderMode(
              {
                width,
                height,
                cut: false,
              },
              vType,
            )
          }
          setPlaying(true)
        }

        // TODO: hack 监听视频变化，改变播放大小
        if (sdom) {
          sdom.bindResize && sdom.removeEventListener('resize', sdom.bindResize)
          sdom.bindResize = () => {
            initBoxSizeD()
          }
          sdom.addEventListener('resize', sdom.bindResize)
        }
      },
      { wait: 10 },
    )

    useEffect(() => {
      if (!rtcInfo.isMySelf && vType === 'video') {
        // 远程视频流按需订阅；
        // todo docVisibility !== 'visible', 时是否订阅,切换tab还有明显播放过程。
        rtc?.subscribeVideoStreamsByYxuid(rtcInfo?.yxuid, !!inViewPort)
      }
    }, [inViewPort, docVisibility, rtcInfo?.isMySelf, rtcInfo?.yxuid, ablePlay, rtcInfo.stream?.[vType]])

    const stop = useMemoizedFn(async () => {
      const { stream } = rtcInfo
      setPlaying(false)
      cancel?.()
      if (stream && videoRef.current) {
        stream?.stop(vType)
      }
    })

    const { run: play, cancel } = useDebounceFna(
      async () => {
        const { stream, isMySelf } = rtcInfo
        if (
          inViewPort &&
          ((vType === viewBoxType.screen && rtcInfo.isShareScreen) ||
            (vType === viewBoxType.video && rtcInfo.isOpenVideo))
        ) {
          if (!stream?.Play) {
            return
          }
          // const { videoDom, screenDom } = stream.Play;
          const videoDom = stream.Play?.video?.dom
          const screenDom = stream.Play?.screen?.dom
          const sdom = vType === viewBoxType.screen ? screenDom : videoDom
          if ((stream && !playing) || (stream && !sdom)) {
            stop()

            if (isMySelf) {
              // 本地播放
              if (vType === viewBoxType.video && rtcInfo.isOpenVideo) {
                // 播放视频
                console.log('localStream.play111')
                await stream?.play(videoRef.current, { video: true, screen: false })
              } else if (vType === viewBoxType.screen && rtcInfo.isShareScreen) {
                // 播放屏幕共享
                await stream?.play(videoRef.current, { screen: true, video: false })
              }
            } else {
              // 开始播放前先停止旧容器的播放,避免冲突播放，被 SDK 忽略
              try {
                if (vType === viewBoxType.video && rtcInfo.isOpenVideo) {
                  await stream.play(videoRef.current, { video: true, screen: false })
                } else if (vType === viewBoxType.screen && rtcInfo.isShareScreen) {
                  await stream.play(videoRef.current, { screen: true, video: false })
                }
              } catch (err) {
                console.warn(err)
              }
            }
            initBoxSizeD()
          }
        } else {
          stop()
        }
      },
      { wait: 20 },
    )

    useEffect(() => {
      if (window.isWeb) {
        play()
      }
    }, [meeting?.viewMode, type, yxuid, meeting?.screenUserInfo?.yxUid])

    useEffect(() => {
      if (window.isMobile) {
        stop()
        setTimeout(() => {
          console.log(`${yxuid}viewbox-延迟的播放`)
          play()
        }, 400)
      }
    }, [window.isMobile])

    useEffect(() => {
      // docVisibility，修复页面最小化或者因此是，播放不正常的问题。
      docVisibility === 'visible' && initBoxSizeD()
    }, [rtcInfo.isShareScreen, rtcInfo.isOpenVideo, vWidth, vHeight, inViewPort, docVisibility])

    // @ts-ignore
    useEffect(() => {
      return stop
    }, [])

    useEffect(() => {
      const handle = () => {
        stop()
        setTimeout(() => {
          play()
        }, 400)
      }
      eventBus.on(ViewBoxEvent.Play, handle)
      return () => {
        eventBus.off(ViewBoxEvent.Play, handle)
      }
    }, [])

    useEffect(() => {
      const handle = () => {
        setTimeout(() => {
          stop()
          play()
        }, 500)
      }
      eventBus.on(ViewBoxEvent.PlayFast, handle)
      return () => {
        eventBus.off(ViewBoxEvent.PlayFast, handle)
      }
    }, [])

    // 根据视图大小切换视频播放的大小；
    const streamType = useMemo(() => {
      const streamType =
        sizeType === sizeTypes.small || (vHeight && vHeight <= 200) ? WebRTC2.STREAM_TYPE.LOW : WebRTC2.STREAM_TYPE.HIGH
      return streamType
    }, [sizeType, vHeight])

    const handleIos17Play = async () => {
      if (!firstPlayRef.current) {
        return
      }
      const stream = rtcInfo.stream
      if (rtcInfo?.isOpenVideo && vWidth && vHeight) {
        await stream.close({ type: 'video' })
        stream.open({ type: 'video' }).then(() => {
          videoRef.current && stream?.play(videoRef.current, { video: true })
          firstPlayRef.current = false
        })
      }
    }
    useEffect(() => {
      if (isMobile && compareVersions(ua.os.version, '17') > 0) {
        handleIos17Play()
      }
    }, [rtcInfo?.isOpenVideo, vHeight, vWidth])

    useEffect(() => {
      if (!rtcInfo.isMySelf && rtcInfo.stream?.[vType] && vType === 'video') {
        rtc?.client?.setRemoteStreamType(rtcInfo.stream, streamType, 'video')
      }
    }, [rtcInfo.isMySelf, rtcInfo.stream?.[vType], vType, streamType])

    const videoClass = React.useMemo(() => {
      return classnames(Style['video-wrap'], {
        [Style['no-mirror']]: !!meeting?.screenUid,
        [Style.visible]: ablePlay,
        [Style['mirror-rotate']]: rtcInfo.isMySelf && rtc?.isBackCamera,
        [Style['self-mirror']]: rtcInfo.isMySelf,
      })
    }, [ablePlay, meeting?.screenUid, rtcInfo.isMySelf, rtc?.isBackCamera])

    const VideoContainer = React.useMemo(() => {
      return <div key={yxuid + type} className={videoClass} ref={videoRef} />
    }, [type, yxuid, videoRef, videoClass])

    // TODO 译员 这个数据要改到获取的人员列表中, 重构列表的时候需要注意
    const interpreter = useMemo(() => {
      // 同声传译开启的时候才展示译员标签
      return simultaneous?.interpretationStatus === InterpretationStatus.starting
        ? members?.interpretersObj[yxuid] || {}
        : {}
    }, [yxuid, members?.interpretersObj, simultaneous?.interpretationStatus])

    const getLanguagesLabel = () => {
      const curLocale = getLocale()
      const isZh = curLocale === 'zh-CN'
      const { outputChannelLanguage, fromLanguageTag, toLanguageTag, fromLanguageNameShort, toLanguageNameShort } =
        interpreter
      if (outputChannelLanguage === '') {
        return formatMessage({ id: 'INTERPRETERS' })
      } else if (outputChannelLanguage === fromLanguageTag) {
        return `${formatMessage({ id: 'INTERPRETERS' })}·${
          isZh ? fromLanguageNameShort?.charAt(0) : fromLanguageNameShort
        }`
      } else if (outputChannelLanguage === toLanguageTag) {
        return `${formatMessage({ id: 'INTERPRETERS' })}·${isZh ? toLanguageNameShort?.charAt(0) : toLanguageNameShort}`
      }
    }

    const showFocusedIcon = useMemo(() => {
      if (isMobile) {
        return false
      }
      return (focused?.focusedYuxid === user?.yxuid && user?.yxuid === yxuid) || focused?.focusedYuxid === yxuid
    }, [focused?.focusedYuxid, user?.yxuid, yxuid, meeting?.viewMode])

    // useEffect(() => {
    //   console.log(
    //     'rtcInfo.stream?.pubStatus?.[vType]?.consumerId',
    //     `${yxuid}- ${rtcInfo.stream?.pubStatus?.[vType]?.consumerId}`
    //   );
    // }, [rtcInfo.stream?.pubStatus?.[vType]?.consumerId]);

    // console.log(type, !window.isMobile || type !== viewBoxType.screen, 'type');
    return (
      <div
        {...otherProps}
        ref={viewRef}
        className={classnames(className, {
          [Style['only-share']]: meeting?.viewMode === MODE.ONLYSHARE,
          [Style.video]: type === viewBoxType.video,
          [Style['view-box']]: sizeType !== sizeTypes.small,
          [Style['small-box']]: sizeType === sizeTypes.small,
          [Style['full-box']]: sizeType === sizeTypes.full,
          [Style['is-talking']]: isTalking,
          [Style['able-click']]: otherProps.onClick,
          [Style['only-one']]: meeting && meeting.memberCount <= 1,
        })}
        data-yxuid={yxuid}
      >
        {window.isMobile && type === viewBoxType.screen ? (
          <TransformWrapper panning={{ velocityDisabled: true }} doubleClick={{ mode: 'reset' }}>
            <TransformComponent
              wrapperClass={classnames({
                [Style['hide-o']]: !ablePlay,
              })}
              wrapperStyle={{ width: '100%', height: '100%', position: 'absolute' }}
            >
              <div
                className={Style['transform-wrap']}
                style={{
                  width: viewRef.current?.offsetWidth || 0,
                  height: viewRef.current?.offsetHeight || 0,
                }}
              >
                {VideoContainer}
              </div>
            </TransformComponent>
          </TransformWrapper>
        ) : (
          VideoContainer
        )}
        <div className={classnames(Style.inner, { [Style.hide]: ablePlay || !bHeight })}>
          {!ablePlay && (
            <Avatar
              type={rtcInfo.isOpenAudio ? (isTalking ? 'speaking' : 'micOn') : 'micOff'}
              size={bHeight && avatarSize > bHeight * avatarSizeMaxHeight ? bHeight * avatarSizeMaxHeight : avatarSize}
              name={rtcInfo.name}
              className={Style['avatar-box']}
              deviceType={rtcInfo.deviceType}
              url={rtcInfo.avatarUrl}
            />
          )}
        </div>
        {(!window.isMobile || type !== viewBoxType.screen) && (
          <>
            {showFocusedIcon && (
              <Tooltip
                title={formatMessage({ id: 'spotLighted' })}
                placement="bottomLeft"
                className={Style['look-at-tooltip']}
              >
                <div className={classnames(Style['user-focused'], 'focusedIcon')}>
                  <Icon name="icon-focus_solid_1" size={20} mode="symbol" className={Style['user-focused-icon']}></Icon>
                </div>
              </Tooltip>
            )}

            <div
              className={classnames(Style['user-info'], {
                [Style['small-user-info']]: sizeType === sizeTypes.small || (bHeight && avatarSize > bHeight / 2),
              })}
            >
              {rtcInfo.isBadNetwork ? (
                <Icon
                  className={`${Style['weak-network-icon']}`}
                  name="icon-signal_weak_nor"
                  size={16}
                  style={{ color: '#FF6161' }}
                />
              ) : rtcInfo.isOpenAudio ? (
                // isTalking ? (
                //   <Icon
                //     className={Style['c-icon']}
                //     name="iconmicro_solid"
                //     size={14}
                //     style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                //   />
                // ) : (
                //   <Icon className={Style['talk-icon']} name="iconmicro_solid" size={14} />
                // )
                <Icon
                  className={Style['c-icon']}
                  name="icon-micro_solid"
                  size={14}
                  style={{ color: 'rgba(255, 255, 255, 0.8)', marginRight: '4px' }}
                />
              ) : null}
              {rtcInfo.isShareScreen && (
                <Icon className={Style['c-icon']} name="icon-a-Mirrorshare" size={14} style={{ color: '#45E588' }} />
              )}
              <div className={Style.info}>
                <span className={Style.name}>{rtcInfo.name}</span>
                <PersonLabel
                  isMe={rtcInfo.isMySelf}
                  isJoinHost={rtcInfo.role === RoleType.COHOST}
                  isList={false}
                  prefix="("
                  suffix=")"
                  isTranslator={!!interpreter.uid}
                  languagesLabel={getLanguagesLabel()}
                />
              </div>
              {/* <div>yxuid:{yxuid}</div> */}
              {/* <div style={{ color: 'red' }}>
                30分钟权重: {speakWeight?.userWeightMap.get(yxuid)?.totalWeight}
              </div>
              <div style={{ color: 'green' }}>
                2分钟权重: {speakWeight?.userWeightMap.get(yxuid)?.speakTotalWeight}
              </div>
              yuxid: {yxuid} */}
              {/* yuxid: {yxuid} */}
            </div>
            {showEmoJ && (
              <EmoJ
                yxuid={yxuid}
                boxWidth={viewRef.current?.offsetWidth}
                boxHeight={viewRef.current?.offsetHeight}
              ></EmoJ>
            )}
          </>
        )}
      </div>
    )
  }),
)
export default ViewBox
